function getPath(source, path) {
  return String(path || '')
    .split('.')
    .filter(Boolean)
    .reduce((value, key) => value?.[key], source)
}

function toText(value) {
  return String(value ?? '').trim()
}

function toNumber(value) {
  if (value === undefined || value === null || value === '')
    return undefined
  const numberValue = Number(value)
  return Number.isFinite(numberValue) ? numberValue : undefined
}

function compactTextIds(...values) {
  const result = []
  values.flat(Infinity).forEach((value) => {
    String(value ?? '')
      .split(',')
      .map(item => item.trim())
      .filter(Boolean)
      .forEach(item => result.push(item))
  })
  return [...new Set(result)]
}

export function buildTbPixelPurchaseDetailQuery(record = {}) {
  const recordId = toText(record.orderId)
  const knownOrderIds = compactTextIds(record.outerOrderId, record.orderIds)
  const orderId = knownOrderIds[0]
  const purchaseId = !orderId || orderId !== recordId ? recordId : ''

  return {
    orderId: orderId || undefined,
    purchaseId: purchaseId || undefined,
    orderCode: toText(record.orderCode) || undefined,
  }
}

function firstPositiveNumber(...values) {
  for (const value of values) {
    const numberValue = toNumber(value)
    if (numberValue !== undefined && numberValue > 0)
      return numberValue
  }
  return undefined
}

function roundCurrency(value) {
  const numberValue = toNumber(value)
  return numberValue === undefined ? undefined : Math.round(numberValue * 100) / 100
}

function getStatusText(detail) {
  return [
    'orderStatus',
    'orderStatusName',
    'status',
    'statusName',
    'refundStatus',
    'refundStatusName',
    'baseInfo.status',
    'baseInfo.statusName',
    'baseInfo.refundStatus',
    'baseInfo.refundStatusName',
    'baseInfo.closeReason',
    'baseInfo.closeRemark',
  ]
    .map(path => toText(getPath(detail, path)).toLowerCase())
    .filter(Boolean)
    .join('|')
}

function hasCancelOrRefundText(detail) {
  return /refund|after.?sale|售后|售後|退款|cancel|close|terminated|取消|关闭|關閉|终止|終止/.test(
    getStatusText(detail),
  )
}

function hasRefundStatus(value) {
  const status = toText(value).toLowerCase()
  return Boolean(status) && ![
    '0',
    'false',
    'none',
    'normal',
    'no_refund',
    'not_refund',
  ].includes(status)
}

function getTbAmount(detail, fallbackAmount) {
  const purchaseMoney = toNumber(getPath(detail, 'purchaseMoney'))
  const commissionFee = toNumber(getPath(detail, 'commissionFee'))
  const amountInMinorUnits = firstPositiveNumber(
    getPath(detail, 'payMoney'),
    purchaseMoney !== undefined ? purchaseMoney + (commissionFee || 0) : undefined,
    getPath(detail, 'orderMoney'),
    getPath(detail, 'totalRealPayPrice.amount'),
  )
  return roundCurrency(
    amountInMinorUnits !== undefined
      ? amountInMinorUnits / 100
      : firstPositiveNumber(fallbackAmount),
  )
}

export function resolveTbPixelPurchaseDecision(detail, fallbackAmount) {
  if (!detail)
    return { type: 'pending', reason: 'tb_detail_empty' }

  const orderStatus = toText(getPath(detail, 'orderStatus')).toUpperCase()
  const detailResults = getPath(detail, 'detailResults') || []
  const hasRefundResult = Array.isArray(detailResults)
    && detailResults.some(item => item?.refundResult || hasCancelOrRefundText(item))

  if (hasRefundResult || hasCancelOrRefundText(detail))
    return { type: 'discard', detail, reason: 'tb_cancel_or_refund' }

  const paidStatuses = new Set([
    'WAIT_SELLER_SEND_GOODS',
    'WAIT_BUYER_CONFIRM_GOODS',
    'TRADE_SUCCESS',
    'TRADE_FINISHED',
    'TRADE_BUYER_SIGNED',
    'SELLER_CONSIGNED_PART',
  ])
  const pendingStatuses = new Set([
    'WAIT_BUYER_P',
    'WAIT_BUYER_PAY',
    'BULIDING',
    'BUILDING',
  ])
  const amount = getTbAmount(detail, fallbackAmount)
  // payMoney is the amount payable and may already be populated while a
  // third-party payment is still waiting for the buyer. It is therefore not
  // evidence that payment completed.
  const hasConfirmedPayment = paidStatuses.has(orderStatus)
    || Boolean(toText(getPath(detail, 'payTime')))

  if (hasConfirmedPayment && amount && amount > 0)
    return { type: 'report', detail, amount }
  if (pendingStatuses.has(orderStatus))
    return { type: 'pending', detail, reason: 'tb_unpaid' }
  return { type: 'pending', detail, reason: 'tb_status_unknown' }
}

function getAlibabaAmount(detail, fallbackAmount) {
  const baseAmount = toNumber(getPath(detail, 'baseInfo.totalAmount'))
  const commissionFee = toNumber(getPath(detail, 'commissionFee'))
  const amountWithCommission = baseAmount !== undefined
    ? baseAmount + (commissionFee || 0) / 100
    : undefined
  return roundCurrency(
    firstPositiveNumber(
      amountWithCommission,
      baseAmount,
      getPath(detail, 'payMoney'),
      getPath(detail, 'purchaseMoney'),
      fallbackAmount,
    ),
  )
}

export function resolveAlibabaPixelPurchaseDecision(detail, fallbackAmount) {
  if (!detail)
    return { type: 'pending', reason: 'alibaba_detail_empty' }

  const baseStatus = toText(
    getPath(detail, 'baseInfo.status')
    || getPath(detail, 'orderStatus')
    || getPath(detail, 'status'),
  ).toLowerCase()
  const refundStatus = getPath(detail, 'baseInfo.refundStatus') ?? getPath(detail, 'refundStatus')
  const productItems = getPath(detail, 'productItems') || getPath(detail, 'detailResults') || []
  const hasRefundItem = Array.isArray(productItems)
    && productItems.some(item => (
      toNumber(item?.refund)
      || hasRefundStatus(item?.refundStatus)
      || hasCancelOrRefundText(item)
    ))

  if (
    ['cancel', 'terminated', 'closed', 'close', 'cancelled', 'canceled'].includes(baseStatus)
    || hasRefundStatus(refundStatus)
    || hasRefundItem
    || hasCancelOrRefundText(detail)
  ) {
    return { type: 'discard', detail, reason: 'alibaba_cancel_or_refund' }
  }

  const amount = getAlibabaAmount(detail, fallbackAmount)
  const tradeTerms = getPath(detail, 'tradeTerms') || []
  const hasPaidTradeTerm = Array.isArray(tradeTerms)
    && tradeTerms.some(term => /paid|pay|success|已支付|已付款/.test(
      toText(term?.payStatus || term?.payWayDesc).toLowerCase(),
    ))
  const paidStatuses = new Set([
    'waitsellersend',
    'waitbuyerreceive',
    'confirm_goods',
    'success',
  ])
  const hasPayInfo = Boolean(toText(
    getPath(detail, 'baseInfo.payTime') || getPath(detail, 'payTime'),
  )) || hasPaidTradeTerm || Number(getPath(detail, 'payMoney') || 0) > 0

  if ((paidStatuses.has(baseStatus) || hasPayInfo) && amount && amount > 0)
    return { type: 'report', detail, amount }
  if (baseStatus === 'waitbuyerpay' || Number(getPath(detail, 'status')) === 0)
    return { type: 'pending', detail, reason: 'alibaba_unpaid' }
  return { type: 'pending', detail, reason: 'alibaba_status_unknown' }
}

function getWeidianAmount(detail, fallbackAmount) {
  const orderMoney = toNumber(getPath(detail, 'baseInfo.orderMoney'))
  const shippingFee = toNumber(getPath(detail, 'baseInfo.shippingFee'))
  const commissionFee = toNumber(getPath(detail, 'baseInfo.commissionFee'))
  const componentAmount = [orderMoney, shippingFee, commissionFee]
    .filter(value => value !== undefined)
    .reduce((total, value) => total + value, 0)
  const amountInMinorUnits = firstPositiveNumber(
    componentAmount,
    getPath(detail, 'baseInfo.totalPrice'),
    getPath(detail, 'totalPrice'),
  )
  return roundCurrency(
    amountInMinorUnits !== undefined
      ? amountInMinorUnits / 100
      : firstPositiveNumber(fallbackAmount),
  )
}

export function resolveWeidianPixelPurchaseDecision(detail, fallbackAmount) {
  if (!detail)
    return { type: 'pending', reason: 'weidian_detail_empty' }

  const rawStatus = getPath(detail, 'baseInfo.status') ?? getPath(detail, 'status')
  const status = Number(rawStatus)
  const amount = getWeidianAmount(detail, fallbackAmount)
  const paidStatuses = new Set([1, 2, 5, 6, 12])
  const discardedStatuses = new Set([3, 4, 7, 8, 10, 11])

  if (discardedStatuses.has(status) || hasCancelOrRefundText(detail))
    return { type: 'discard', detail, reason: 'weidian_cancel_or_refund' }
  if (paidStatuses.has(status) && amount && amount > 0)
    return { type: 'report', detail, amount }
  if (status === 0)
    return { type: 'pending', detail, reason: 'weidian_unpaid' }
  return { type: 'pending', detail, reason: 'weidian_status_unknown' }
}
